import React, { useEffect } from 'react';
import { Form } from 'components/ds';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import UserNameField from '../../../components/UserNameField/UserNameField';
import { pageConfigs } from '../../../configs/pageConfigs';
import { useSearchUser } from '../../../hooks/useSearchUser';
import { ValidatorField } from '../../../../../components/ValidatorField';
import { useUsername } from '../../../hooks/useUsername';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { resetStorage } from '../Logout';
import { HelpBox } from '../../../components/HelpBox/HelpBox';
import { useNavigationFlow } from '../../../hooks/useNavigationFlow';
import { useSchemaLoader } from '../../../hooks/useSchemaLoader';

interface FormValues {
  username: string;
}

export const LoginUserName: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const {data: pageConfig = {}} = useGetTemplate(pageConfigs.loginUserName.template);
  const {searchUser, loading} = useSearchUser();
  const {username, saveUsername} = useUsername();
  const {navigateBasedOnUserData} = useNavigationFlow();
  const {waitForSchema} = useSchemaLoader();
  
  const theme = ApiLocalStorage.get('theme') || {};
  
  useEffect(() => {
    if (username) {
      form.setFieldsValue({username});
    }
  }, [form, username]);
  
  useEffect(() => {
    resetStorage();
  }, []);
  
  const validateForm = async (): Promise<FormValues> => {
    try {
      const fields = await form.validateFields();
      const cleanUsername = ValidatorField.removeSpecialCharsFromUsername(fields.username);
      
      return {username: cleanUsername};
    } catch (error) {
      throw new Error('Por favor, preencha todos os campos obrigatórios.');
    }
  };
  
  const validateUser = async (username: string, captcha: string) => {
    try {
      // Aguardar o carregamento do schema antes de processar a navegação
      console.log('validateUser - Waiting for schema to load...');
      await waitForSchema();
      console.log('validateUser - Schema loaded successfully');
      
      const response = await searchUser(username, captcha);
      ApiLocalStorage.set('@pwa-username', username);
      
      if (response) {
        ApiLocalStorage.set('@pwa-participant-id', response?.participantId || '');
        console.log('validateUser - Navigating based on user data:', response);
        await navigateBasedOnUserData(response);
      }
    } catch (error) {
      console.error('Error validating user:', error);
    }
  };
  
  const handleNext = async (): Promise<void> => {
    ApiLocalStorage.set('@pwa-username', username);
    
    try {
      const {username: formUsername} = await validateForm();
      
      if (username !== formUsername) {
        saveUsername(formUsername);
      }
      
      await validateUser(formUsername, '');
    } catch (error: any) {
      console.error('Erro no handleNext:', error);
    }
  };

  const needHelpFml = theme?.login?.['helpBox-fml'];
  const helpBoxConfig = theme?.login?.['helpBox-login-fml'];
  
  return (
    <PageContainer
      title={pageConfig.title}
      navigateBack={pageConfigs?.loginUserName?.navigateBack?.()}
      onClick={handleNext}
      buttonText={pageConfigs.loginUserName.buttonLabel}
      disabled={loading}
      loading={loading}
      extraAboveButton={needHelpFml ? (
        <HelpBox
          customTitle={helpBoxConfig?.title}
          customLinkText={helpBoxConfig?.linkText}
          customIcon={helpBoxConfig?.icon}
        />
      ) : undefined}
      thereIsButton
    >
      <Form form={form} layout="vertical" onFinish={handleNext}>
        <UserNameField form={form} isDisabled={loading}/>
      </Form>
    </PageContainer>
  );
};

export default LoginUserName;
